<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div
    v-if="geofenceGroup"
    id="edit_geofenceGroup"
    class="container-fluid flex-v"
    style="width: 100%; flex-grow: 1">
    <div
      class="row general-section-container"
      :class="{ collapsed: inModal || !isGeneralSectionExpanded }">
      <div class="col-12">
        <div
          class="form-horizontal"
          :class="{ 'pb-4': altStyle }">
          <div
            v-if="!inModal"
            class="form-section group flex-h">
            <span class="icon">
              <template v-if="geofenceGroupType === GeofenceGroupType.AUDIENCE">
                <icon-colored-audience-geofence-group />
              </template>
              <template v-if="geofenceGroupType === GeofenceGroupType.PROXIMITY">
                <icon-colored-geofence-group />
              </template>
            </span>
            <span
              v-if="name == null || name.trim() === ''"
              style="flex-grow: 1">
              <template v-if="geofenceGroupType === GeofenceGroupType.AUDIENCE">
                {{ capitalize($t('actions.newAudienceGeofenceGroup')) }}
              </template>
              <template v-if="geofenceGroupType === GeofenceGroupType.PROXIMITY">
                {{ capitalize($t('actions.newProximityGeofenceGroup')) }}
              </template>
            </span>
            <span
              v-else
              style="flex-grow: 1"
              >{{ name }}</span
            >
            <form-section-expand-collapse
              :expanded="isGeneralSectionExpanded"
              @expanded="expandSection('general')"
              @collapsed="collapseSection('general')" />
          </div>
          <transition name="fade">
            <div
              v-if="isGeneralSectionExpanded"
              class="form-section-content"
              :class="isGeneralSectionExpanded || inModal ? 'form-section-expanded' : 'form-section-collapsed'">
              <!-- NAME -->
              <div class="form-group row">
                <form-label
                  for-i-d="edit_geofenceGroup_name"
                  :is-required="true"
                  :size="altStyle ? 2 : 3"
                  :is-left="altStyle">
                  {{ capitalize($t('names.name', { n: 1 })) }}
                </form-label>
                <div :class="altStyle ? 'col-sm-10' : 'col-sm-8'">
                  <input
                    id="edit_geofenceGroup_name"
                    v-model="name"
                    name="name"
                    type="text"
                    :class="{ 'form-control': true, input: true, 'is-danger': errors.has('name') }" />
                  <span
                    v-show="errors.has('name')"
                    class="help is-danger"
                    >{{ errors.first('name') }}</span
                  >
                </div>
              </div>
              <!-- DESCRIPTION -->
              <div
                v-show="true"
                class="form-group row">
                <form-label
                  for-i-d="edit_geofenceGroup_description"
                  :size="altStyle ? 2 : 3"
                  :is-left="altStyle">
                  {{ capitalize($t('names.description', { n: 1 })) }}
                </form-label>
                <div :class="altStyle ? 'col-sm-10' : 'col-sm-8'">
                  <input
                    id="edit_geofenceGroup_description"
                    v-model="description"
                    name="description"
                    type="text"
                    :class="{ 'form-control': true, input: true, 'is-danger': errors.has('description') }" />
                  <span
                    v-show="errors.has('description')"
                    class="help is-danger"
                    >{{ errors.first('description') }}</span
                  >
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <div
      class="map-and-panes-container flex-grow"
      :class="{ full: !isGeneralSectionExpanded }">
      <geofences-list-and-map
        :is-editable="true"
        :geofence-group="geofenceGroup"
        :in-modal="inModal"
        :max-height="altStyle ? 36 : null"
        @geofence-updated="onGeofenceUpdated" />
      <!-- FOOTER -->
      <save-and-cancel
        style="gap: 1rem"
        v-if="showFooter"
        entity-type="geofenceGroup"
        :justify="altStyle ? 'start' : 'between'"
        :reversed="altStyle"
        @cancel="cancel"
        @save="save" />
    </div>

    <modal
      id="edit_geofenceGroup_save_changes_confirmation_modal"
      :visible="isSaveChangesModalVisible"
      :title="capitalize(isNew ? $t('actions.saveNewEntity') : $t('actions.saveChanges'))"
      :body="capitalize(isNew ? $t('messages.confirmSaveNewEntity') : $t('messages.confirmSaveChanges'))"
      :button1-caption="capitalize($t('actions.yes'))"
      :button2-caption="capitalize($t('actions.no'))"
      :button2-visible="true"
      size="lg"
      @confirmed="saveChangesModalConfirmed"
      @cancelled="saveChangesModalCancelled">
      <template #modal-after-text>
        <EntityChangeList :changes="geofenceGroupChanges" />
      </template>
    </modal>

    <modal
      id="edit_geofenceGroup_update_or_create_copy_confirmation_modal"
      :visible="isUpdateOrCreateCopyModalVisible"
      :title="capitalize($t('actions.saveChanges'))"
      :body="capitalize($t('messages.updateOrCreateGeofenceGroupCopy'))"
      button2-class="primary"
      :button1-caption="capitalize($t('actions.update'))"
      :button2-caption="capitalize($t('actions.createCopy'))"
      :button2-visible="true"
      :button2-callback="createCopyConfirmed"
      :button3-caption="capitalize($t('actions.cancel'))"
      :button3-visible="true"
      @confirmed="saveChangesModalConfirmed"
      @cancelled="updateOrCreateCopyCancelled" />

    <confirm-losing-changes-modal
      :is-visible="isConfirmLosingChangesModalVisible"
      @confirmed="
        () => {
          isConfirmLosingChangesModalVisible = false;
        }
      "
      @cancelled="
        () => {
          isConfirmLosingChangesModalVisible = false;
        }
      " />
  </div>
</template>

<script type="text/babel">
  import { DateTime } from 'luxon';
  import { capitalize } from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  import {
    ConfirmLosingChangesModal,
    FormLabel,
    FormSectionExpandCollapse,
    Modal,
    SaveAndCancel,
  } from '@/common/components/controls';
  import EntityChangeList from '@/common/components/EntityChangeList';
  import {
    IconColoredAudienceGeofenceGroup,
    IconColoredDataPoints,
    IconColoredGeofenceGroup,
    IconColoredMobilePhone,
  } from '@/common/icons/colored';
  import Geofence from '@/common/models/geo/Geofence';
  import GeofenceGroupType from '@/common/models/geofenceGroups/GeofenceGroupType.enum';
  import Filter from '@/common/models/query-params/Filter';
  import FilterCollection from '@/common/models/query-params/FilterCollection';
  import QueryParams from '@/common/models/query-params/QueryParams';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import UserNotifications from '@/utils/UserNotifications';
  import MessageCollection from '@/validator/MessageCollection';
  import i18n from '@/vueI18n';

  import GeofencesListAndMap from './GeofencesListAndMap';

  export default {
    components: {
      ConfirmLosingChangesModal,
      EntityChangeList,
      FormLabel,
      FormSectionExpandCollapse,
      GeofencesListAndMap,
      IconColoredAudienceGeofenceGroup,
      IconColoredGeofenceGroup,
      Modal,
      SaveAndCancel,
    },
    props: {
      geofenceGroupType: {
        type: Number,
      },
      geofenceGroupID: {
        type: Number,
        default: null,
      },
      inModal: {
        type: Boolean,
        default: false,
      },
      showFooter: {
        type: Boolean,
        default: true,
      },
      submitEmits: {
        type: Boolean,
        default: false,
      },
      isDuplicate: {
        type: Boolean,
        default: false,
      },
      altStyle: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['complete'],
    data() {
      return {
        entityTimestamp: DateTime.local(),
        errors: new MessageCollection(),
        geofenceGroup: null,
        geofenceGroupChanges: [],
        isConfirmLosingChangesModalVisible: false,
        isGeneralSectionExpanded: true,
        isDuplicatingExistingGeofence: this.isDuplicate && (this.geofenceGroupID || this.$route.params.fromGroupID),
        isNew:
          (this.$route.params.id === 'new' || this.inModal) &&
          this.geofenceGroupID == null &&
          this.$route.params.fromGroupID == null,
        isSaveChangesModalVisible: false,
        isUpdateOrCreateCopyModalVisible: false,
        lineItems: [],
        saveChangesConfirmationModalID: 'edit_geofenceGroup_save_changes_confirmation_modal',
        didDetectUnexpectedChanges: false,
        totalDataPointsCount: null,
        totalDevicesCount: null,
        validationResult: {},
        GeofenceGroupType,
        IconColoredMobilePhone,
        IconColoredDataPoints,
      };
    },
    computed: {
      ...mapGetters('orgApp', ['enableAudienceGeoStatistics']),
      name: {
        get() {
          return this.geofenceGroup !== null ? this.geofenceGroup.name : null;
        },
        set(value) {
          this.updateGeofenceGroupName({ geofenceGroup: this.geofenceGroup, value });
        },
      },
      description: {
        get() {
          return this.geofenceGroup !== null ? this.geofenceGroup.description : null;
        },
        set(value) {
          this.updateGeofenceGroupDescription({ geofenceGroup: this.geofenceGroup, value });
        },
      },
      listEndpoint() {
        return this.path;
      },
      detailsEndpoint() {
        return `${this.path}/${this.geofenceGroup.id}`;
      },
      saveChangesConfirmationModalTitle() {
        const message = this.isNew ? 'saveNewEntity' : 'saveChanges';
        return capitalize(i18n.global.t(`actions.${message}`));
      },
      saveChangesConfirmationModalBody() {
        const message = this.isNew ? 'confirmSaveNewEntity' : 'confirmSaveChanges';
        return capitalize(i18n.global.t(`messages.${message}`));
      },
      geofences() {
        return this.geofenceGroup.geofences;
      },
      path() {
        return this.geofenceGroupType === GeofenceGroupType.AUDIENCE
          ? '/audienceGeofenceGroups'
          : '/proximityGeofenceGroups';
      },
    },
    methods: {
      ...mapActions([
        'updateGeofenceGroupDescription',
        'updateGeofenceGroupGeofences',
        'updateGeofenceGroupName',
        'updateGeofenceGroupTags',
        'updateGeofenceGeoJson',
        'updateGeofenceGeofenceGroupID',
      ]),
      ...mapActions('orgApp', [
        'createNewGeofenceGroup',
        'getGeofenceGroup',
        'getLineItems',
        'saveGeofenceGroup',
        'saveGeofences',
      ]),
      capitalize(text) {
        return capitalize(text);
      },
      async save() {
        const extras = {};
        //
        // Validate and prompt for saving changes if necessary.
        //
        this.errors.clear();
        try {
          await this.$validator.validateAll(this.geofenceGroup, extras);
          if (this.isDuplicatingExistingGeofence) {
            await this.createCopyConfirmed();
          } else if (this.geofenceGroup.id === -1) {
            await this.saveChangesModalConfirmed();
          } else if (this.lineItems.length > 0) {
            this.isUpdateOrCreateCopyModalVisible = true;
          } else {
            this.geofenceGroupChanges = await this.geofenceGroup.getChanges();
            this.isSaveChangesModalVisible = true;
          }
        } catch {
          const message = `${i18n.global.t('messages.failedToSaveGeofenceGroup')}<br/>${this.$validator.messageBag.htmlErrorList}`;
          UserNotifications.error({
            title: i18n.global.t('messages.missingRequiredValue'),
            message,
          });
          this.errors = this.$validator.messageBag.errors;
        }
      },
      cancel() {
        this.exit();
      },
      exit() {
        if (this.submitEmits) {
          this.$emit('complete');
        } else if (!this.isNew) {
          this.$router.push(this.detailsEndpoint);
        } else {
          this.$router.push(this.listEndpoint);
        }
      },
      async saveChangesModalConfirmed() {
        try {
          const geofenceGroup = await this.saveGeofenceGroup({ geofenceGroup: this.geofenceGroup });
          this.entityTimestamp = DateTime.fromISO(this.geofenceGroup.timestamp);

          //
          // update geofences geofenceGroupID
          //
          const geofencesWithGeofenceGroupID = this.geofenceGroup.geofences.filter(
            (x) => x.geofenceGroupID != null && x.geofenceGroupID > -1,
          );
          const geofencesWithoutGeofenceGroupID = this.geofenceGroup.geofences
            .filter((x) => x.geofenceGroupID === null || x.geofenceGroupID === -1)
            .map(
              (x) =>
                new Geofence(
                  x.id,
                  geofenceGroup.id,
                  x.name,
                  x.description,
                  x.tags,
                  x.lat,
                  x.lng,
                  x.boundsSwLat,
                  x.boundsSwLng,
                  x.boundsNeLat,
                  x.boundsNeLng,
                  x.geoJson,
                  x.placeSearchJson,
                  x.placeSearchProvider,
                  x.createdOnUtc,
                  x.modifiedOnUtc,
                  x.createdByUserID,
                  x.modifiedByUserID,
                  x.mapboxDrawID,
                  null,
                  x.dynamicGeofences,
                ),
            );

          const geofences = [...geofencesWithoutGeofenceGroupID, ...geofencesWithGeofenceGroupID];
          await Promise.allSettled([
            this.updateGeofenceGroupGeofences({
              geofenceGroup,
              value: geofences,
            }),
            this.updateGeofenceGroupGeofences({
              geofenceGroup: this.geofenceGroup,
              value: geofences,
            }),
          ]);

          await this.saveGeofences(this.geofenceGroup);

          // Touch geofencegroup to trigger geofence-site processing
          await this.saveGeofenceGroup({ geofenceGroup });
          this.completeSaveOperation(geofenceGroup);
          return geofenceGroup;
        } finally {
          this.isSaveChangesModalVisible = false;
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
      },
      async createCopyConfirmed() {
        try {
          const clone = this.geofenceGroup.clone(this.isDuplicatingExistingGeofence);
          const clonedGeofenceGroup = await this.saveGeofenceGroup({ geofenceGroup: clone });
          this.entityTimestamp = DateTime.fromISO(this.geofenceGroup.timestamp);

          //
          // update geofences geofenceGroupID
          //
          const geofences = this.geofenceGroup.geofences.map(
            (x) =>
              new Geofence(
                -1,
                clonedGeofenceGroup.id,
                x.name,
                x.description,
                x.tags,
                x.lat,
                x.lng,
                x.boundsSwLat,
                x.boundsSwLng,
                x.boundsNeLat,
                x.boundsNeLng,
                x.geoJson,
                x.placeSearchJson,
                x.placeSearchProvider,
                null,
                null,
                null,
                null,
                x.mapboxDrawID,
              ),
          );

          this.updateGeofenceGroupGeofences({
            geofenceGroup: clonedGeofenceGroup,
            value: geofences,
          });

          await this.saveGeofences(clonedGeofenceGroup);

          // Touch geofencegroup to trigger geofence-site processing
          await this.saveGeofenceGroup({ geofenceGroup: clonedGeofenceGroup });
          this.completeSaveOperation(clonedGeofenceGroup);
          return clonedGeofenceGroup;
        } catch (e) {
          this.isSaveChangesModalVisible = false;
          return null;
        }
      },
      completeSaveOperation(geofenceGroup) {
        this.isSaveChangesModalVisible = false;
        this.entityTimestamp = DateTime.fromISO(geofenceGroup.timestamp);

        if (this.submitEmits) {
          this.$emit('complete', geofenceGroup);
        } else if (!this.inModal) {
          this.$router.push(geofenceGroup.endpoint);
        }
        UserNotifications.success({
          title: capitalize(i18n.global.t('names.success', { n: 1 })),
          message: i18n.global.t('messages.geofenceGroupSavedSuccessfully'),
        });
      },
      saveChangesModalCancelled() {
        this.isSaveChangesModalVisible = false;
      },
      updateOrCreateCopyCancelled() {
        this.isUpdateOrCreateCopyModalVisible = false;
      },
      expandSection(sectionName) {
        switch (sectionName) {
          case 'general':
            this.isGeneralSectionExpanded = true;
            break;
          default:
            break;
        }
      },
      collapseSection(sectionName) {
        switch (sectionName) {
          case 'general':
            this.isGeneralSectionExpanded = false;
            break;
          default:
            break;
        }
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 1500);
      },
      initPage() {
        setTimeout(() => {
          if (this.isNew) {
            window.document.getElementById('edit_geofenceGroup_name').focus();
          }
        }, 250);
      },
      fetchLineItems() {
        const queryParams = QueryParams.all();
        queryParams.filterCollection = new FilterCollection([
          new Filter('lineitem_geofencegroups.geofencegroup_id', [this.geofenceGroup.id]),
        ]);

        this.getLineItems({ queryParams }).then(
          (result) => {
            this.lineItems = result.items;
          },
          () => {
            // Do nothing
          },
        );
      },
      onGeofenceUpdated() {
        // Triggers a timestamp update, so that changes are detected when saving.

        this.name = this.name;
      },
    },
    async beforeMount() {
      if (this.isNew) {
        window.document.title = capitalize(i18n.global.t('actions.newGeofenceGroup'));
        this.geofenceGroup = await this.createNewGeofenceGroup(this.geofenceGroupType);

        this.$validator.init(this.geofenceGroup);
        this.initPage();
      } else {
        const id = this.geofenceGroupID || this.$route.params.fromGroupID || this.$route.params.id;
        this.geofenceGroup = await this.getGeofenceGroup({ id });
        if (this.isDuplicatingExistingGeofence) {
          this.geofenceGroup.name = `Copy of ${this.geofenceGroup.name}`;
        } else {
          window.document.title = `${window.document.title}: ${this.geofenceGroup.name}`;
        }
        this.entityTimestamp = DateTime.fromISO(this.geofenceGroup.timestamp);
        this.$validator.init(this.geofenceGroup);
        this.initPage();
        this.fetchLineItems();

        if (!this.isDuplicatingExistingGeofence) {
          //
          // When editing, make sure the original object has not changed after the initial load.
          //
          this.didDetectUnexpectedChanges = !this.geofenceGroup.isPristine();
          if (this.didDetectUnexpectedChanges) {
            UserNotifications.unexpectedChangeDetected();
          }
        }
      }
    },
    beforeRouteLeave(to, from, next) {
      // if (this.entityTimestamp.equals(this.geofenceGroup.timestamp)) {
      //   next();
      // } else {
      //   this.isConfirmLosingChangesModalVisible = true;
      //   this.confirmLosingChangesCallback = next;
      // }
      next();
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  .general-section-container {
    flex-basis: 21rem;
    flex-shrink: 0;

    &.collapsed {
      flex-basis: 8.5rem;
    }
  }

  .map-and-panes-container {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 38rem);
    max-height: calc(100vh - 38rem);

    &.full {
      top: 15rem;
      height: calc(100% - 10.8rem);
    }

    .save-and-cancel {
      margin-bottom: 1.6rem;
    }
  }

  #edit_geofenceGroup {
    > .actions {
      margin-top: 1.6rem;
      margin-bottom: -0.8rem;
      display: flex;
      flex-direction: row-reverse;
    }
  }

  .audience-stats {
    margin-top: 1.6rem;
    margin-bottom: -1rem;
    flex-shrink: 0;
  }
</style>
